import _ from 'lodash';
import { fetchJsonWithDefaultHeaders } from 'common/http';
import { defaultHeaders } from 'common/http/index';
import { fetchJson } from 'common/http';
import { coreUidFromAssetId } from 'common/cetera/catalog_id';
import {
  APPROVALS,
  ApprovalState,
  Status,
  SubmissionObject,
  SubmissionOutcomeStatus,
  WorkflowTaskScope,
  ResubmissionSettings,
  WorkflowTargetAudience
} from 'common/core/approvals_enums';
import { AudienceScope, ViewPermissions } from 'common/types/view';
import {
  Guidance,
  GuidanceSummary,
  GuidanceSummaryV2,
  GuidanceV2,
  Workflow,
  WorkflowGuidance,
  WorkflowRecordListing,
  WorkflowTask
} from 'common/types/approvals';
import {
  getDomain,
  userAndTeamAutocompleteUrl
} from 'common/components/AccessManager/Util';
import DomainRights from 'common/types/domainRights';
import { currentUserHasRight } from 'common/current_user';


/**
 * PUBLIC API
 */

/**
 * @param assetId: catalog asset id; is one of:
 *    - 4x4 (all published assets, working copies)
 *    - 4x4:<revision seq> (revisions)
 *    - 4x4:draft (story draft)
 * @returns guidanceResponse:  see
 *    https://approvalsapi1.docs.apiary.io/#reference/0/view-approvals/get-submission-guidance-for-an-asset
 */
export const fetchApprovalsGuidance = (assetId: string): Promise<GuidanceSummary> => {
  const coreUid = coreUidFromAssetId(assetId);
  const apiPath = `/api/views/${coreUid}/approvals?method=guidance&assetId=${assetId}`;
  const options: RequestInit = {
    credentials: 'same-origin',
    headers: defaultHeaders
  };

  return fetchJson(apiPath, options);
};

/**
 * @param assetId: catalog asset id; is one of:
 *    - 4x4 (all published assets, working copies)
 *    - 4x4:<revision seq> (revisions)
 *    - 4x4:draft (story draft)
 * @returns guidanceResponse:  see
 *    TODO Update this link: https://approvalsapi1.docs.apiary.io/#reference/0/view-approvals/get-submission-guidance-for-an-asset
 */
export const fetchApprovalsGuidanceV2 = (assetId: string): Promise<GuidanceSummaryV2> => {
  const coreUid = coreUidFromAssetId(assetId);
  const apiPath = `/api/views/${coreUid}/approvals?method=guidance_v2&assetId=${assetId}`;
  const options: RequestInit = {
    credentials: 'same-origin',
    headers: defaultHeaders
  };

  return fetchJson(apiPath, options);
};

/**
 * For any given asset (be that published or draft), it may only be pending in one queue
 * This function returns the internal guidance if it has the pending record, else the public guidance
 *
 * @param guidance  Guidance summary passed to `withGuidanceV2`
 */
export const choosePendingGuidance = (guidance: GuidanceSummaryV2) => {
  if (guidance.internal !== undefined && assetIsPendingV2(guidance.internal))
    return guidance.internal;
  else
    return guidance.public;
};

/**
 * Will return the asset ID for the given View.
 *
 * This takes into account two special cases of assets that we have in our system...
 *
 * DSMAPI revisions are different from "working copies" in that they do not have a lens backing them and so do not
 * have a unique UID. Instead, they have a revision number that is appended to the base lens UID.
 *
 * Story drafts are unique in much the same way, except there can only be one draft per story, and this is signified
 * by appending `:draft` to the base lens UID.
 *
 * Note that core is _mostly_ unaware of these special cased UIDs, except in the case of the approvals guidance endpoint.
 * The catalog is also aware of these special UIDs.
 *
 * @param coreViewId Base UID of a View from core
 * @param revisionSeq A revision sequence, used only for DSMAPI revisions
 * @param isStoryDraft Whether or not the asset is a story draft.
 */
export const assetIdFor = (coreViewId: string, revisionSeq?: string | number, isStoryDraft?: boolean) => {
  const isRevision = _.isFinite(_.toNumber(revisionSeq));
  if (isRevision && isStoryDraft) {
    throw new Error('an asset cannot be both a revision and a story draft');
  }

  if (isRevision) {
    return `${coreViewId}:${revisionSeq}`;
  } else if (isStoryDraft) {
    return `${coreViewId}:draft`;
  } else {
    return coreViewId;
  }
};

/**
 * Example usage:
 *   fetchApprovalsGuidanceV2(coreViewId, revision).then(guidance => {
 *     withGuidance(guidance).withdraw();
 *   });
 *
 * Why like this? Set guidance in component state where necessary,
 * then use this method to make approvals requests using that guidance.
 */
export const withGuidance = (guidanceResponse: GuidanceSummary) => {
  return {
    // Async
    approve: (notes = '') => approveAsset(guidanceResponse, notes),
    reject: (notes = '') => rejectAsset(guidanceResponse, notes),
    withdraw: () => withdrawAsset(guidanceResponse),
    submitForApproval: () => submitAnyAvailableRequest(guidanceResponse),
    submitChangeAudienceRequest: () => changeAudienceRequest(guidanceResponse),
    submitUpdatePublishedAssetRequest: () => updatedPublishedAssetRequest(guidanceResponse),
    // Boolean (synchronous)
    willEnterApprovalQueue: () => willEnterApprovalQueue(guidanceResponse),
    sitesThatWillBeFederatedToIfMadePublic: () => sitesThatWillBeFederatedToIfMadePublic(guidanceResponse),
    sitesThatWillBeFederatedToIfApprovedToPublic: () =>
      sitesThatWillBeFederatedToIfApprovedToPublic(guidanceResponse),
    canSubmitForApproval: () => canSubmit(guidanceResponse),
    canSubmitChangeAudienceRequest: () => canSubmitChangeAudience(guidanceResponse),
    canSubmitUpdatePublishedAssetRequest: () => canSubmitUpdatePublished(guidanceResponse),
    isPending: () => assetIsPending(guidanceResponse),
    // Helper
    publishedUid: () => publishedUid(guidanceResponse)
  };
};

/**
 * Example usage:
 *   fetchApprovalsGuidanceV2(coreViewId, revision).then(guidance => {
 *     withGuidance(guidance).withdraw();
 *   });
 *
 * Why like this? Set guidance in component state where necessary,
 * then use this method to make approvals requests using that guidance.
 */
export const withGuidanceV2 = (guidanceResponse: WorkflowGuidance) => {
  return {
    // Async
    approve: (notes = '') => approveAssetV2(guidanceResponse, notes),
    reject: (notes = '') => rejectAssetV2(guidanceResponse, notes),
    withdraw: () => withdrawAssetV2(guidanceResponse),
    submitForApproval: () => submitAnyAvailableRequestV2(guidanceResponse),
    submitChangeAudienceRequest: () => changeAudienceRequestV2(guidanceResponse),
    submitUpdatePublishedAssetRequest: () => updatedPublishedAssetRequestV2(guidanceResponse),
    // Boolean (synchronous)
    willEnterApprovalQueue: () => willEnterApprovalQueueV2(guidanceResponse),
    sitesThatWillBeFederatedToIfMadePublic: () => sitesThatWillBeFederatedToIfMadePublicV2(guidanceResponse),
    sitesThatWillBeFederatedToIfApprovedToPublic: () =>
      sitesThatWillBeFederatedToIfApprovedToPublicV2(guidanceResponse),
    canSubmitForApproval: () => canSubmitV2(guidanceResponse),
    canSubmitChangeAudienceRequest: () => canSubmitChangeAudienceV2(guidanceResponse),
    canSubmitUpdatePublishedAssetRequest: () => canSubmitUpdatePublishedV2(guidanceResponse),
    isPending: () => assetIsPendingV2(guidanceResponse),
    // Helper
    publishedUid: () => publishedUidV2(guidanceResponse),
    hasGuidance: (!!guidanceResponse && assertHasGuidanceV2(guidanceResponse))
  };
};

/**
 * Fetch approvals settings from core.
 *
 * This is actually a list of "Workflows" that are set up on the domain.
 * When assets are submitted for approval, they are submitted to one of these workflows.
 */
export const fetchSettings = (): Promise<Workflow[]> => {
  const apiPath = '/api/approvals';
  const options: RequestInit = {
    credentials: 'same-origin',
    headers: defaultHeaders
  };

  return fetchJson(apiPath, options);
};

/**
 * Fetch user roles from core; used when generating list of approvers on
 * admin/approvals/settings page.
 *
 */
export const fetchRoles = (): Promise<any[]> => {
  const apiPath = '/api/roles';
  const options: RequestInit = {
    credentials: 'same-origin',
    headers: defaultHeaders
  };

  return fetchJson(apiPath, options);
};

/**
 * Remove approval rights for a specific reviewer on a given workflow
 *
 * @param uid Four x four of user to remove rights from
 * @param workflowId Workflow to update
 */
export const removeReviewer = (uid: string, workflowId: number) => {
  const apiPath = `/api/approvals/${workflowId}?method=removeReviewer&userId=${uid}`;
  const options: RequestInit = {
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'DELETE'
  };

  return fetch(apiPath, options);
};

/**
 * Add approval right for a specific reviewer on a given workflow
 *
 * @param uid Four x four of user to remove rights from
 * @param workflowId Workflow to update
 */
export const addReviewer = (uid: string, workflowId: number) => {
  const apiPath = `/api/approvals/${workflowId}?method=addReviewer&userId=${uid}`;
  const options: RequestInit = {
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'POST'
  };

  return fetch(apiPath, options);
};

/**
 * Use catalog to get autocomplete results from user search. I know this isn't an
 * approvals endpoint but this method doesn't live anywhere else and we use it in the
 * settings page, so I'm putting it here ¯\_(ツ)_/¯
 *
 * @param userSearch A string from the search box (ex. a partial name of a user)
 */
export const fetchUsersForAutocomplete = (userSearch: string) => {
  const userRights: DomainRights[] = [DomainRights.view_others_datasets, DomainRights.edit_others_datasets];

  return fetchJsonWithDefaultHeaders(userAndTeamAutocompleteUrl(
    userSearch,
    getDomain(),
    {disabled: false, rights: userRights, includeTeams: false})
  );
};

/**
 * Returns whether or not the given provenance will result in requiring manual approval.
 *
 * @param settingsResponse The current workflow that approvals is using
 * @param provenance The desired provenance of the asset
 */
export const provenanceRequiresManualApproval = (settingsResponse: Workflow, provenance: WorkflowTaskScope) =>
  _.get(getProvenanceTask(settingsResponse, provenance), 'presetState') === APPROVALS.STATUS.PENDING;

/**
 * willEnterApprovalQueue is the result of withGuidance(approvalsGuidance).willEnterApprovalQueue()
 * this is needed because withGuidance's willEnterApprovalQueue only tells you
 * whether or not the asset is considered pending and could enter the queue
 * if 'require manual approval' is enabled
 */
export const isPublicAssetThatRequiresApproval = (
  willEnterApprovalQueue: boolean,
  permissions: ViewPermissions
) => {
  const wouldBePublic = permissions.scope == AudienceScope.Public;
  return willEnterApprovalQueue && wouldBePublic;
};

/**
 * Get the approval workflow task for the given provenance.
 *
 * Note: Scope is the same as provenance; it is called 'scope' in the API response
 *
 * @param apiResponse The approvals workflow to get the provenance task for
 * @param scope The provenance (scope) to get the task for
 */
export const getProvenanceTask = (apiResponse: Workflow, scope: WorkflowTaskScope) => {
  const task = _.find(_.get(apiResponse, '[0].steps[0].tasks'), _.matches({ scope }));
  if (task) {
    return { taskId: task.id, presetState: task.presetState };
  } else {
    return null;
  }
};

/**
 * Change the preset state for a specific approvals workflow task.
 *
 * The preset state controls what happens when an asset enters the approvals queue.
 *
 * @param taskId The task to update
 * @param value The new preset state to set for the task
 * @return A promise that will resolve with the updated approval worfklow task
 */
export const setPresetState = (taskId: number, presetState: ApprovalState) =>
  updateTask(taskId, { presetState });

/**
 * Change the resubmission police to a specific approvals workflow task.
 *
 * The resubmission policy is what happens when an asset is resubmitted after being approved
 * (this can currently happen with data updates or visibility changes, depending on other settings)
 *
 * @param workflowId The workflow to update
 * @param value The new resubmission policy for the task.
 */
export const setApprovedResubmissionPolicy = (
  workflowId: number,
  approvedResubmissionPolicy: ResubmissionSettings
) => updateWorkflow(workflowId, { approvedResubmissionPolicy });

/**
 * Poll the approvals API to see if approval for an asset has completed yet.
 * This returns a promise the either resolves when approval is finished, or
 * rejects after a certain number of retries are hit.
 *
 * @param assetUid Asset to poll for
 * @param submissionId The ID of the submission to check for completion of
 * @param maxRetries Number of times to retry hitting the API (default: 30)
 * @param sleepMs How many milliseconds to wait between tries (default: 2000)
 */
export async function pollForCompletedApproval(
  assetUid: string,
  submissionId: number,
  maxRetries = 30,
  sleepMs = 2000
): Promise<SubmissionOutcomeStatus | undefined> {
  if (maxRetries === 0) {
    return Promise.reject('pollForCompletedApproval reached max retries');
  }

  // inclusion of this no-op param (t) is a workaround to get ATS to not cache
  // see EN-40081 for more details or EN-41712 for the bigger picture solution
  const t = new Date().getTime();

  const fourByFour = coreUidFromAssetId(assetUid);
  const apiPath = `/api/views/${fourByFour}/approvals?includeExpired=true&no_cache=true&t=${t}`;
  const resp = (await fetchJson(apiPath, { headers: defaultHeaders })) as WorkflowRecordListing;
  const submission = resp.summaries.find((summary) => summary.approvalWorkflowSubmissionId === submissionId);

  const { SUCCESS, FAILURE } = APPROVALS.SUBMISSION_OUTCOME_STATUS;
  const submissionComplete =
    submission?.submissionOutcomeStatus && [SUCCESS, FAILURE].includes(submission.submissionOutcomeStatus);

  if (submissionComplete) {
    return await submission?.submissionOutcomeStatus;
  } else {
    await sleep(sleepMs);
    return pollForCompletedApproval(assetUid, submissionId, maxRetries - 1);
  }
}

/** Sleep for the given number of milliseconds */
function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * PRIVATE METHODS
 */

/**
 * These are keys in the `GuidanceSummary` object that can be used to
 * get guidance responses that can be used to submit an asset.
 *
 * We need this type because we need to tell TypeScript exactly _what_ values we expect to get out of
 * `GuidanceSummary` when checking against `SUBMISSION_KEYS`.
 */
enum SubmissionKey {
  toChangeAudience = 'toChangeAudience',
  toUpdatePublishedAsset = 'toUpdatePublishedAsset',
  toPublishToPublic = 'toPublishToPublic'
}

/**
 * This array contains the keys into a `GuidanceSummary` object that can be used to submit an asset.
 *
 * "Submit" here means that the asset is being submitted to an approvals workflow.
 */
const SUBMISSION_KEYS: SubmissionKey[] = [
  SubmissionKey.toChangeAudience,
  SubmissionKey.toUpdatePublishedAsset,
  SubmissionKey.toPublishToPublic
];

enum SubmissionKeyV2 {
  toChangeAudience = 'toChangeAudience',
  toUpdatePublishedAsset = 'toUpdatePublishedAsset',
  toPublishAndChangeAudience = 'toPublishAndChangeAudience'
}

const SUBMISSION_KEYS_V2: SubmissionKeyV2[] = [
  SubmissionKeyV2.toChangeAudience,
  SubmissionKeyV2.toUpdatePublishedAsset,
  SubmissionKeyV2.toPublishAndChangeAudience
];

// Settings
/**
 * Update a specific workflow task.
 *
 * @param taskId ID of the task to update
 * @param body Values to set for the task. This can be a partial task to only update some fields.
 * @return A promise that will resolve with the updated task
 */
const updateTask = (taskId: number, body: Partial<WorkflowTask>): Promise<WorkflowTask> => {
  const apiPath = `/api/approvals?method=updateTask&taskId=${taskId}`;

  const options: RequestInit = {
    body: JSON.stringify(body),
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'PUT'
  };

  return fetchJson(apiPath, options);
};

/**
 * Update a specific workflow.
 *
 * @param workflowId ID of the workflow to update
 * @param body Values to set for the workflow. This can be a partial workflow to only update some fields.
 */
const updateWorkflow = (workflowId: number, body: Partial<Workflow>): Promise<Workflow> => {
  body.targetAudience = WorkflowTargetAudience.PUBLIC;
  const apiPath = `/api/approvals/${workflowId}`;

  const options: RequestInit = {
    body: JSON.stringify(body),
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'PUT'
  };

  return fetchJson(apiPath, options);
};

// Helpers to check for ability to be submitted for various approvals

/**
 * Checks if an asset *could* enter the queue
 * e.g., if you are updating a public asset with 'require manual approval' set to true
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const willEnterApprovalQueue = (guidanceResponse: GuidanceSummary) =>
  guidanceResponse &&
  SUBMISSION_KEYS.some((key) => guidanceResponse[key]?.expectedState === APPROVALS.STATUS.PENDING);

/**
 * Checks if an asset *could* enter the queue
 * e.g., if you are updating a public asset with 'require manual approval' set to true
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const willEnterApprovalQueueV2 = (guidanceResponse: WorkflowGuidance) =>
  guidanceResponse &&
  SUBMISSION_KEYS_V2.some(
    (key) => guidanceResponse[key]?.queueOptions[0].expectedState === APPROVALS.STATUS.PENDING
  );

/**
 * Returns a list of other sites that this asset will end up federating to if its visibility is changed.
 *
 * Currently, only public assets are federated so this only handles making something public
 * (either during publish or an audience change after making it public)
 *
 * This is mainly used by the AccessManager to show a message when making an asset public.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const sitesThatWillBeFederatedToIfMadePublic = (guidanceResponse: GuidanceSummary) => {
  // this will return the "toChangeAudience" or "toPublishToPublic" object
  // which includes a submission which has an "object"
  const viewerChangeDetails = guidanceResponse?.toChangeAudience || guidanceResponse?.toPublishToPublic;

  // this will contain a "submission object" of which we only care about "public_audience_request"
  // once we need to add internal approvals, this will be an adventure
  const submissionObject: SubmissionObject | undefined = viewerChangeDetails?.submission?.object;

  if (submissionObject === SubmissionObject.PUBLIC_AUDIENCE_REQUEST) {
    return viewerChangeDetails?.expectedFederatedTo || [];
  }

  return [];
};

/**
 * Returns a list of other sites that this asset will end up federating to if its visibility is changed.
 *
 * Currently, only public assets are federated so this only handles making something public
 * (either during publish or an audience change after making it public)
 *
 * This is mainly used by the AccessManager to show a message when making an asset public.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const sitesThatWillBeFederatedToIfMadePublicV2 = (guidanceResponse: WorkflowGuidance) => {
  // this will return the "toChangeAudience" or "toPublishAndChangeAudience" object
  // which includes a submission which has an "object"
  const viewerChangeDetails =
    guidanceResponse?.toChangeAudience || guidanceResponse?.toPublishAndChangeAudience;

  // this will contain a "submission object" of which we only care about "public_audience_request"
  // once we need to add internal approvals, this will be an adventure
  const submissionObject: SubmissionObject | undefined = viewerChangeDetails?.submission?.object;

  if (submissionObject === SubmissionObject.PUBLIC_AUDIENCE_REQUEST) {
    return viewerChangeDetails?.expectedFederatedTo || [];
  }

  return [];
};

/**
 * Returns a list of domains that this asset will end up federating to if it is approved for public visibility.
 *
 * This is mainly used in the approvals queue UI to show where an asset will federate to when it is approved.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const sitesThatWillBeFederatedToIfApprovedToPublic = (guidanceResponse: GuidanceSummary) => {
  return guidanceResponse?.ifApproved?.expectedFederatedTo || [];
};

/**
 * Returns a list of domains that this asset will end up federating to if it is approved for public visibility.
 *
 * This is mainly used in the approvals queue UI to show where an asset will federate to when it is approved.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const sitesThatWillBeFederatedToIfApprovedToPublicV2 = (guidanceResponse: WorkflowGuidance) => {
  return guidanceResponse?.ifApproved?.expectedFederatedTo || [];
};

/**
 * Returns whether or not an asset can be submitted _in some way_ to approvals.
 *
 * See the SUBMISSION_KEYS array for possible parts of the guidance summary that can be considered "submissions".
 * To check for specific submission availabilities, see `canSubmitChangeAudience` and `canSubmitUpdatePublished`
 *
 * NOTE: If this is not passed a guidance summary, it will throw an error!
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const canSubmit = (guidanceResponse: GuidanceSummary) => {
  assertHasGuidance(guidanceResponse);
  return SUBMISSION_KEYS.some((key) => guidanceResponse[key]);
};

/**
 * Returns whether or not an asset can be submitted _in some way_ to approvals.
 *
 * See the SUBMISSION_KEYS array for possible parts of the guidance summary that can be considered "submissions".
 * To check for specific submission availabilities, see `canSubmitChangeAudience` and `canSubmitUpdatePublished`
 *
 * NOTE: If this is not passed a guidance summary, it will throw an error!
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const canSubmitV2 = (guidanceResponse: WorkflowGuidance) => {
  assertHasGuidanceV2(guidanceResponse);
  return SUBMISSION_KEYS.some((key) => guidanceResponse[key]);
};

/**
 * Returns whether or not an asset can be submitted for an audience change
 * (this is also called "visibility" or "scope", which is _not_ to be confused with the "scope" that is also used in this same file to mean "provenance").
 *
 * NOTE: If this is not passed a guidance summary, it will throw an error!
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const canSubmitChangeAudience = (guidanceResponse: GuidanceSummary) => {
  assertHasGuidance(guidanceResponse);
  return !!guidanceResponse.toChangeAudience;
};

/**
 * Returns whether or not an asset can be submitted for an audience change
 * (this is also called "visibility" or "scope", which is _not_ to be confused with the "scope" that is also used in this same file to mean "provenance").
 *
 * NOTE: If this is not passed a guidance summary, it will throw an error!
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const canSubmitChangeAudienceV2 = (guidanceResponse: WorkflowGuidance) => {
  assertHasGuidanceV2(guidanceResponse);
  return !!guidanceResponse.toChangeAudience;
};

/**
 * Returns whether or not an asset can be submitted to be published.
 * This is only relevant for assets that have already been published and are being updated.
 *
 * NOTE: If this is not passed a guidance summary, it will throw an error!
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const canSubmitUpdatePublished = (guidanceResponse: GuidanceSummary) => {
  assertHasGuidance(guidanceResponse);
  return !!guidanceResponse.toUpdatePublishedAsset;
};

/**
 * Returns whether or not an asset can be submitted to be published.
 * This is only relevant for assets that have already been published and are being updated.
 *
 * NOTE: If this is not passed a guidance summary, it will throw an error!
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const canSubmitUpdatePublishedV2 = (guidanceResponse: WorkflowGuidance) => {
  assertHasGuidanceV2(guidanceResponse);
  return !!guidanceResponse.toUpdatePublishedAsset;
};

/**
 * Returns whether an asset is currently pending approval.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const assetIsPending = (guidanceResponse: GuidanceSummary) => {
  assertHasGuidance(guidanceResponse);
  return guidanceResponse.currentState === APPROVALS.STATUS.PENDING;
};

/**
 * Returns whether an asset is currently pending approval.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const assetIsPendingV2 = (guidanceResponse: WorkflowGuidance) => {
  assertHasGuidanceV2(guidanceResponse);
  return guidanceResponse.currentState === APPROVALS.STATUS.PENDING;
};

/**
 * Returns whether the current user can review the public/internal workflows
 *
 * @returns object containing the following attributes:
 * - public: Boolean indicating whether the user has the `review_public_approvals` right
 * - internal: Boolean indicating whether the user has the `review_internal_approvals` right
 * - length: The number of workflows the user is able to review. Named to allow array-like checks.
 * - totalWorkflows: The total number of workflows a reviewer could potentially have rights for.
 */
export const couldReviewWorkflows = () => {
  const response = {
    public: false,
    internal: false,
    length: 0,
    totalWorkflows: 2
  };

  if (currentUserHasRight(DomainRights.review_public_approvals)) {
    response.public = true;
    response.length++;
  }

  if (currentUserHasRight(DomainRights.review_internal_approvals)) {
    response.internal = true;
    response.length++;
  }

  return response;
};

/**
 * Get the published UID of a draft asset.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const publishedUid = (guidanceResponse: GuidanceSummary) => {
  if (!canSubmit(guidanceResponse)) {
    throw new Error(
      `guidanceResponse has no submission object to parse: ${JSON.stringify(guidanceResponse)}`
    );
  }

  const targetKey = SUBMISSION_KEYS.find((key) => guidanceResponse[key]);
  return targetKey && guidanceResponse[targetKey]?.publishedViewUid;
};

/**
 * Get the published UID of a draft asset.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const publishedUidV2 = (guidanceResponse: WorkflowGuidance) => {
  if (!canSubmitV2(guidanceResponse)) {
    throw new Error(
      `guidanceResponse has no submission object to parse: ${JSON.stringify(guidanceResponse)}`
    );
  }

  const targetKey = SUBMISSION_KEYS.find((key) => guidanceResponse[key]);
  return targetKey && guidanceResponse[targetKey]?.publishedViewUid;
};

/**
 * Throws an error if passed an empty object (`undefined`, `null`, or `{}`)
 *
 * This is used in many other functions in this file to asset that we are given a guidance summary.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const assertHasGuidance = (guidanceResponse?: GuidanceSummary) => {
  if (_.isEmpty(guidanceResponse)) {
    throw new Error('no guidanceResponse provided to withGuidance helper');
  }
};

/**
 * Throws an error if passed an empty object (`undefined`, `null`, or `{}`)
 *
 * This is used in many other functions in this file to asset that we are given a guidance summary.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const assertHasGuidanceV2 = (guidanceResponse?: WorkflowGuidance): boolean => {
  const guidanceIsEmpty = _.isEmpty(guidanceResponse);

  if (guidanceIsEmpty) {
    throw new Error('no guidanceResponse provided to withGuidance helper');
  }

  return !guidanceIsEmpty;
};

// Approve/Reject, a.k.a. Updating Approval Records

/** Used when approving/rejecting an asset that is in the approvals queue */
interface UpdateApprovalRequestBody {
  /** New state to set for asset */
  state: Status;

  /** Any notes to add to the asset when approving/rejecting it */
  notes: string;
}

/**
 * Approve an asset (for visibility change, updating published version, etc.)
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 * @param notes Notes to add when approving.
 */
const approveAsset = (guidanceResponse: GuidanceSummary, notes = '') =>
  updateApproval(guidanceResponse, {
    state: APPROVALS.STATUS.APPROVED,
    notes
  });

/**
 * Approve an asset (for visibility change, updating published version, etc.)
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 * @param notes Notes to add when approving.
 */
const approveAssetV2 = (guidanceResponse: WorkflowGuidance, notes = '') =>
  updateApprovalV2(guidanceResponse, {
    state: APPROVALS.STATUS.APPROVED,
    notes
  });

/**
 * Reject an asset (for visibility change, updating published version, etc.)
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 * @param notes Notes to add when rejecting.
 */
const rejectAsset = (guidanceResponse: GuidanceSummary, notes = '') =>
  updateApproval(guidanceResponse, {
    state: APPROVALS.STATUS.REJECTED,
    notes
  });

/**
 * Reject an asset (for visibility change, updating published version, etc.)
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 * @param notes Notes to add when rejecting.
 */
const rejectAssetV2 = (guidanceResponse: WorkflowGuidance, notes = '') =>
  updateApprovalV2(guidanceResponse, {
    state: APPROVALS.STATUS.REJECTED,
    notes
  });

/**
 * Update an asset's approval status.
 * This can be used to either approve or reject assets.
 *
 * See also: `approveAsset` and `rejectAsset`
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 * @param putBody Parameters to use to update an asset's approval status
 */
const updateApproval = (guidanceResponse: GuidanceSummary, putBody: UpdateApprovalRequestBody) => {
  if (_.isEmpty(guidanceResponse)) {
    return Promise.reject('no guidanceResponse provided to withGuidance helper');
  }

  if (!guidanceResponse.updateUrl) {
    return Promise.reject('updateUrl not found in guidance response');
  }

  const apiPath = guidanceResponse.updateUrl;
  const options: RequestInit = {
    body: JSON.stringify(putBody),
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'PUT'
  };

  return fetch(apiPath, options);
};

/**
 * Update an asset's approval status.
 * This can be used to either approve or reject assets.
 *
 * See also: `approveAsset` and `rejectAsset`
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 * @param putBody Parameters to use to update an asset's approval status
 */
const updateApprovalV2 = (guidanceResponse: WorkflowGuidance, putBody: UpdateApprovalRequestBody) => {
  if (_.isEmpty(guidanceResponse)) {
    return Promise.reject('no guidanceResponse provided to withGuidance helper');
  }

  if (!guidanceResponse.updateUrl) {
    return Promise.reject('updateUrl not found in guidance response');
  }

  const apiPath = guidanceResponse.updateUrl;
  const options: RequestInit = {
    body: JSON.stringify(putBody),
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'PUT'
  };

  return fetch(apiPath, options);
};

/**
 * Withdraw (a.k.a. cancelWorkflowSubmission) an approval request for an asset.
 * This will remove it from the approvals queue.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const withdrawAsset = (guidanceResponse: GuidanceSummary) => {
  if (_.isEmpty(guidanceResponse)) {
    return Promise.reject('no guidanceResponse provided to withGuidance helper');
  }

  if (!guidanceResponse.withdrawUrl) {
    return Promise.reject('withdrawUrl not found in guidance response');
  }

  const apiPath = guidanceResponse.withdrawUrl;
  const options: RequestInit = {
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'DELETE'
  };

  return fetch(apiPath, options);
};

/**
 * Withdraw (a.k.a. cancelWorkflowSubmission) an approval request for an asset.
 * This will remove it from the approvals queue.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const withdrawAssetV2 = (guidanceResponse: WorkflowGuidance) => {
  if (_.isEmpty(guidanceResponse)) {
    return Promise.reject('no guidanceResponse provided to withGuidance helper');
  }

  if (!guidanceResponse.withdrawUrl) {
    return Promise.reject('withdrawUrl not found in guidance response');
  }

  const apiPath = guidanceResponse.withdrawUrl;
  const options: RequestInit = {
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'DELETE'
  };

  return fetch(apiPath, options);
};

// Approvals requests, a.k.a. startWorkflowSubmission

/**
 * Given a guidance summary, this will find the proper submission details and then will
 * submit an asset for approval.
 *
 * This can result in any of the keys in `SUBMISSION_KEYS` being used to submit the asset.
 * At the time of writing, this uses one of:
 * - toChangeAudience: Changing the audience (a.k.a. visibility) of an asset. For example, making it public or site-scoped (a.k.a. internal)
 * - toUpdatePublishedAsset: Making an update to an already published asset.
 * - toPublishToPublic: Publishing an asset for the first time _and_ making it public.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const submitAnyAvailableRequest = (guidanceResponse: GuidanceSummary) => {
  // It should be the case that only one submission key is present in a guidanceResponse
  const targetKey = SUBMISSION_KEYS.find((key) => guidanceResponse[key]);
  const guidance = targetKey && guidanceResponse[targetKey];

  if (!guidance) {
    return Promise.reject(
      `no submissions available for guidanceResponse: ${JSON.stringify(guidanceResponse)}`
    );
  }

  return executeApprovalsRequest(guidanceResponse, guidance);
};

/**
 * Given a guidance summary, this will find the proper submission details and then will
 * submit an asset for approval.
 *
 * This can result in any of the keys in `SUBMISSION_KEYS` being used to submit the asset.
 * At the time of writing, this uses one of:
 * - toChangeAudience: Changing the audience (a.k.a. visibility) of an asset. For example, making it public or site-scoped (a.k.a. internal)
 * - toUpdatePublishedAsset: Making an update to an already published asset.
 * - toPublishAndChangeAudience: Publishing an asset for the first time _and_ submit it to the relevant queue.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const submitAnyAvailableRequestV2 = (guidanceResponse: WorkflowGuidance) => {
  // It should be the case that only one submission key is present in a guidanceResponse
  const targetKey = SUBMISSION_KEYS_V2.find((key) => guidanceResponse[key]);
  const guidance = targetKey && guidanceResponse[targetKey];

  if (!guidance) {
    return Promise.reject(
      `no submissions available for guidanceResponse: ${JSON.stringify(guidanceResponse)}`
    );
  }

  return executeApprovalsRequestV2(guidanceResponse, guidance);
};

/**
 * Executes a change audience request using the given guidance summary's `toChangeAudience` guidance
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const changeAudienceRequest = (guidanceResponse: GuidanceSummary) => {
  if (!guidanceResponse.toChangeAudience) {
    return Promise.reject("key 'toChangeAudience' not found in guidance response");
  }

  return executeApprovalsRequest(guidanceResponse, guidanceResponse.toChangeAudience);
};

/**
 * Executes a change audience request using the given guidance summary's `toChangeAudience` guidance
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const changeAudienceRequestV2 = (guidanceResponse: WorkflowGuidance) => {
  if (!guidanceResponse.toChangeAudience) {
    return Promise.reject("key 'toChangeAudience' not found in guidance response");
  }

  return executeApprovalsRequestV2(guidanceResponse, guidanceResponse.toChangeAudience);
};

/**
 * Executes an update published asset request using the given guidance summary's `toUpdatePublishedAsset` guidance
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 */
const updatedPublishedAssetRequest = (guidanceResponse: GuidanceSummary) => {
  if (!guidanceResponse.toUpdatePublishedAsset) {
    return Promise.reject("key 'toUpdatePublishedAsset' not found in guidance response");
  }

  return executeApprovalsRequest(guidanceResponse, guidanceResponse.toUpdatePublishedAsset);
};

/**
 * Executes an update published asset request using the given guidance summary's `toUpdatePublishedAsset` guidance
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 */
const updatedPublishedAssetRequestV2 = (guidanceResponse: WorkflowGuidance) => {
  if (!guidanceResponse.toUpdatePublishedAsset) {
    return Promise.reject("key 'toUpdatePublishedAsset' not found in guidance response");
  }

  return executeApprovalsRequestV2(guidanceResponse, guidanceResponse.toUpdatePublishedAsset);
};

/**
 * Given a guidance summary and a guidance object, this will use the guiance object's submission URL to
 * make an actual submission to the approvals API.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidance`
 * @param guidance Guidance object to get submission URL from
 */
const executeApprovalsRequest = (guidanceResponse: GuidanceSummary, guidance: Guidance) => {
  if (_.isEmpty(guidanceResponse)) {
    return Promise.reject('no guidanceResponse provided to withGuidance helper');
  }

  return submitFromGuidance(guidance);
};

/**
 * Given a guidance summary and a guidance object, this will use the guiance object's submission URL to
 * make an actual submission to the approvals API.
 *
 * @param guidanceResponse Guidance summary passed to `withGuidanceV2`
 * @param guidance Guidance object to get submission URL from
 */
const executeApprovalsRequestV2 = (guidanceResponse: WorkflowGuidance, guidance: GuidanceV2) => {
  if (_.isEmpty(guidanceResponse) || _.isEmpty(guidanceResponse)) {
    return Promise.reject('no guidanceResponse provided to withGuidance helper');
  }

  return submitFromGuidanceV2(guidance);
};

/**
 * Returns a promise from fetch that will hit the approvals API to submit an asset to an approvals workflow.
 *
 * @param guidance Guidance object to get submission URL from
 */
const submitFromGuidance = (guidanceObject: Guidance) => {
  const apiPath = guidanceObject.submissionUrl;
  const options: RequestInit = {
    body: JSON.stringify(guidanceObject.submission),
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'POST'
  };

  return fetch(apiPath, options);
};

/**
 * Returns a promise from fetch that will hit the approvals API to submit an asset to an approvals workflow.
 *
 * @param guidance Guidance object to get submission URL from
 */
const submitFromGuidanceV2 = (guidanceObject: GuidanceV2) => {
  if (!guidanceObject.queueOptions || _.isEmpty(guidanceObject.queueOptions[0])) {
    return Promise.reject('no queueOptions provided to submitFromGuidanceV2');
  }
  const apiPath = guidanceObject.queueOptions[0].submissionUrl;
  const options: RequestInit = {
    body: JSON.stringify(guidanceObject.submission),
    credentials: 'same-origin',
    headers: defaultHeaders,
    method: 'POST'
  };

  return fetch(apiPath, options);
};
